import { forwardRef, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type {
  ChangeEvent,
  CSSProperties,
  FocusEvent,
  KeyboardEvent,
  MutableRefObject,
  SelectHTMLAttributes,
} from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cx } from '@shared/lib/cx'
import { FieldFeedback } from '../Field/FieldFeedback'
import { resolveFieldFeedback } from '../Field/field-feedback'
import type { FieldStatus } from '../Field/field-feedback'
import {
  chevron,
  control,
  field,
  label as labelClass,
  labelInvalid,
  menu,
  nativeSelect,
  optionItem,
  optionLabel,
  selectedIcon,
  trigger,
  valueText,
} from './Select.css'

export type SelectOption = {
  label: string
  value: string
}

export type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: string
  helperText?: string
  error?: string
  status?: FieldStatus
  message?: string
  placeholder?: string
  options: SelectOption[]
}

function toSelectValue(value: SelectHTMLAttributes<HTMLSelectElement>['value']): string {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return String(value[0] ?? '')
  return String(value)
}

function setForwardedRef<T>(ref: React.ForwardedRef<T>, value: T) {
  if (typeof ref === 'function') {
    ref(value)
    return
  }

  if (ref) {
    ;(ref as MutableRefObject<T>).current = value
  }
}

const MENU_GAP_PX = 6
const MENU_VIEWPORT_MARGIN_PX = 12
const MENU_MAX_HEIGHT_PX = 256
const MENU_MIN_HEIGHT_PX = 132

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  {
    label,
    helperText,
    error,
    status,
    message,
    placeholder,
    options,
    className,
    id,
    value,
    defaultValue,
    onChange,
    onBlur,
    name,
    disabled,
    required,
    autoFocus,
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const selectId = id ?? generatedId
  const listboxId = `${selectId}-listbox`
  const feedbackId = `${selectId}-feedback`
  const nativeSelectRef = useRef<HTMLSelectElement | null>(null)
  const fieldRef = useRef<HTMLDivElement | null>(null)
  const feedback = resolveFieldFeedback({ error, helperText, message, status })
  const controlled = value !== undefined
  const items = placeholder ? [{ label: placeholder, value: '' }, ...options] : options
  const initialValue = toSelectValue(
    value ?? defaultValue ?? (placeholder ? '' : (options[0]?.value ?? '')),
  )
  const [internalValue, setInternalValue] = useState(initialValue)
  const [open, setOpen] = useState(false)
  const [menuPlacement, setMenuPlacement] = useState<'down' | 'up'>('down')
  const [menuMaxHeight, setMenuMaxHeight] = useState(MENU_MAX_HEIGHT_PX)
  const selectedValue = controlled ? toSelectValue(value) : internalValue
  const selectedOption = items.find((option) => option.value === selectedValue)
  const selectedIndex = Math.max(
    0,
    items.findIndex((option) => option.value === selectedValue),
  )
  const [activeIndex, setActiveIndex] = useState(selectedIndex)

  useEffect(() => {
    setActiveIndex(selectedIndex)
  }, [selectedIndex])

  useEffect(() => {
    if (!open) return

    function handlePointerDown(event: PointerEvent) {
      if (!fieldRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open])

  useLayoutEffect(() => {
    if (!open) return

    function updateMenuGeometry() {
      const rect = fieldRef.current?.getBoundingClientRect()
      if (!rect) return

      const availableBelow =
        window.innerHeight - rect.bottom - MENU_GAP_PX - MENU_VIEWPORT_MARGIN_PX
      const availableAbove = rect.top - MENU_GAP_PX - MENU_VIEWPORT_MARGIN_PX
      const openUp = availableBelow < MENU_MIN_HEIGHT_PX && availableAbove > availableBelow
      const availableSpace = Math.max(openUp ? availableAbove : availableBelow, MENU_MIN_HEIGHT_PX)

      setMenuPlacement(openUp ? 'up' : 'down')
      setMenuMaxHeight(Math.min(MENU_MAX_HEIGHT_PX, Math.floor(availableSpace)))
    }

    updateMenuGeometry()
    window.addEventListener('resize', updateMenuGeometry)
    window.addEventListener('scroll', updateMenuGeometry, true)
    return () => {
      window.removeEventListener('resize', updateMenuGeometry)
      window.removeEventListener('scroll', updateMenuGeometry, true)
    }
  }, [open])

  function getEventTarget(nextValue = selectedValue) {
    if (nativeSelectRef.current) {
      nativeSelectRef.current.value = nextValue
      return nativeSelectRef.current
    }

    return { name, value: nextValue } as HTMLSelectElement
  }

  function selectValue(nextValue: string) {
    if (disabled) return
    if (!controlled) {
      setInternalValue(nextValue)
    }
    setOpen(false)
    onChange?.({
      target: getEventTarget(nextValue),
      currentTarget: getEventTarget(nextValue),
    } as ChangeEvent<HTMLSelectElement>)
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    setOpen(false)
    onBlur?.({
      target: getEventTarget(),
      currentTarget: getEventTarget(),
    } as FocusEvent<HTMLSelectElement>)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return

    if (event.key === 'Escape') {
      setOpen(false)
      return
    }

    if (event.key === 'Tab') {
      setOpen(false)
      return
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      setActiveIndex((current) => {
        const delta = event.key === 'ArrowDown' ? 1 : -1
        return (current + delta + items.length) % items.length
      })
      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (open) {
        selectValue(items[activeIndex]?.value ?? '')
      } else {
        setOpen(true)
      }
    }
  }

  return (
    <div ref={fieldRef} className={cx(field, className)} onBlur={handleBlur}>
      {label ? (
        <label htmlFor={selectId} className={cx(labelClass, feedback.invalid && labelInvalid)}>
          {label}
        </label>
      ) : null}
      <div className={control({ invalid: feedback.invalid })}>
        <button
          id={selectId}
          type="button"
          className={trigger({ invalid: feedback.invalid, open, disabled })}
          aria-controls={listboxId}
          aria-describedby={feedback.message ? feedbackId : undefined}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={feedback.invalid}
          disabled={disabled}
          autoFocus={autoFocus}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={handleKeyDown}
        >
          <span className={valueText({ placeholder: !selectedOption || selectedValue === '' })}>
            {selectedOption?.label ?? placeholder ?? 'Selecione'}
          </span>
          <ChevronDown className={chevron({ open })} size={16} aria-hidden />
        </button>
        <select
          {...rest}
          ref={(element) => {
            nativeSelectRef.current = element
            setForwardedRef(ref, element)
          }}
          className={nativeSelect}
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          value={selectedValue}
          disabled={disabled}
          required={required}
          onChange={() => undefined}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {open ? (
          <div
            id={listboxId}
            className={menu({ placement: menuPlacement })}
            role="listbox"
            aria-labelledby={selectId}
            style={{ '--select-menu-max-height': `${menuMaxHeight}px` } as CSSProperties}
          >
            {items.map((option, index) => {
              const selected = option.value === selectedValue
              return (
                <button
                  key={option.value || '__placeholder'}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={optionItem({
                    active: index === activeIndex,
                    selected,
                    placeholder: option.value === '',
                  })}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectValue(option.value)}
                >
                  <span className={optionLabel}>{option.label}</span>
                  {selected ? <Check className={selectedIcon} size={15} aria-hidden /> : null}
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
      <FieldFeedback id={feedbackId} status={feedback.status}>
        {feedback.message}
      </FieldFeedback>
    </div>
  )
})
